/**
 * Org Overrides Service
 * Wraps correctionsService to provide the same API surface as the old
 * localStorage implementation. Reads are synchronous (against a module-level
 * cache); writes are async and persist to Azure Blob Storage.
 *
 * Call loadCorrections(apiToken) once during app initialisation to populate
 * the cache before any sync reads are needed.
 */
import { getCorrections, setCorrection } from './correctionsService.js';

// Module-level cache: { [employeeId]: managerId | null }
// Populated by loadCorrections(); updated optimistically on write.
let cache = {};

/**
 * Fetch corrections from the backend and warm the module cache.
 * Returns the manager-slice map so callers (useEmployees) can also
 * use department corrections without a second fetch.
 */
export async function loadCorrections(apiToken) {
  const all = await getCorrections(apiToken);
  // Update cache with only the managerId slice
  cache = {};
  for (const [id, entry] of Object.entries(all)) {
    if ('managerId' in entry) cache[id] = entry.managerId;
  }
  return all; // full corrections map, for department use in useEmployees
}

/**
 * Returns the manager-override slice: { [employeeId]: managerId | null }
 * Sync — reads from module-level cache.
 */
export function getOrgOverrides() {
  return { ...cache };
}

/**
 * Set a manager override. Async — persists to backend and updates cache.
 */
export async function setManagerOverride(apiToken, employeeId, managerId) {
  cache[employeeId] = managerId ?? null;
  await setCorrection(apiToken, employeeId, { managerId: managerId ?? null });
}

/**
 * Remove a manager override, reverting to the Graph-sourced manager.
 */
export async function clearManagerOverride(apiToken, employeeId) {
  delete cache[employeeId];
  await setCorrection(apiToken, employeeId, { managerId: null });
}

/**
 * Returns true if an employee has an active manager override.
 */
export function hasOverride(employeeId) {
  return Object.prototype.hasOwnProperty.call(cache, employeeId);
}

/**
 * Resolves the effective manager ID for an employee, applying overrides.
 * Sync — reads from module-level cache.
 */
export function resolveManagerId(employee) {
  if (Object.prototype.hasOwnProperty.call(cache, employee.id)) {
    return cache[employee.id]; // may be null (explicit root)
  }
  return employee.manager?.id || employee.managerId || null;
}
